import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import store from './store'
import App from './App'
import * as Sentry from '@sentry/react'
import { version } from '../package.json'
import getEnv, { isProduction } from './utilities/getEnv'
import '@fontsource/roboto'
import 'Services/localize/i18n'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { PostHogProvider } from 'posthog-js/react'
import PostHog from 'posthog-js'
import { enrichSentryErrorEvent } from 'utilities/sentryLogUtil'
import { t } from 'i18next'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ROUTE from '@constant/route'
import LazyRetry from '@components/LazyRetry/LazyRetry'
import AppRoot from 'AppRoot/AppRoot'
const POSApplication = LazyRetry(() => import('AppRoot/POSApplication'))
const CustomerFacingDisplayPage = LazyRetry(
    () => import('pages/CustomerFacingDisplayPage/CustomerFacingDisplayPage'),
)
const DocumentPage = LazyRetry(() => import('pages/DocumentPage/DocumentPage'))
const RegisterPage = LazyRetry(() => import('pages/RegisterPage'))

if (isProduction()) {
    const serverEnvironment = getEnv('SERVER_SIDE_ENVIRONMENT')
    if (
        serverEnvironment !== undefined &&
        ['Testing', 'UAT', 'Production'].some((x) => x === serverEnvironment)
    ) {
        const overridedVersion = getEnv('WEB_VERSION') // overrided version to display instead of from package.json if any
        Sentry.init({
            dsn: 'https://c58ae6cd06a2484b8463242be43cdd3f@o568528.ingest.sentry.io/5715202',
            integrations: [
                Sentry.browserTracingIntegration(),
                PostHog.sentryIntegration({
                    organization: 'mycos-technologies',
                    projectId: 5715202,
                }),
            ],
            release:
                'orderwire-pos@' +
                (overridedVersion ? overridedVersion : version),
            environment: serverEnvironment,

            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            beforeSend(event, hint) {
                // ignore logging Axios Network error
                if (hint.originalException) {
                    const originalException = hint.originalException as any
                    const exceptionMessage = originalException.message

                    // Block sending HttpError to Sentry
                    if (originalException.httpMetadata) {
                        // log Error message of HttpError
                        console.log(t(exceptionMessage))
                        return null
                    }

                    if (
                        originalException.isAxiosError &&
                        exceptionMessage &&
                        exceptionMessage.toLowerCase().includes('network error')
                    )
                        // return null to block sending error to Sentry
                        return null
                }

                // otherwire, log an error as normally

                // enrich error event, grouping
                enrichSentryErrorEvent(event as any, hint as any)

                // return modified event to Sentry
                return event
            },

            // Set tracesSampleRate to 1.0 to capture 100%
            tracesSampleRate: 1.0,

            // ignore error of 401 axios response
            ignoreErrors: [
                'Request failed with status code 401',
                'Request failed with status code 403',
                // ignore dynamic loading error via React.lazy()
                'Failed to fetch dynamically imported module',
                // ignore some signalR errors which are out-of-control factor (e.g., user's internet connection)
                'Failed to complete negotiation with the server',
                // https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
                // I decided to ignore this error, because we didn't use this Browser API `ResizeObserver` directly in this project
                // So, it properly caused from another library.
                // However, if this cause the problem to user's experience (users report us). We will take a look on this later.
                'ResizeObserver loop',
            ],
        })
    }
}

const serverEnvironment = getEnv('SERVER_SIDE_ENVIRONMENT')
const router = createBrowserRouter(
    [
        {
            path: '/',
            element: <App />,
            children: [
                { path: ROUTE.REGISTER, element: <RegisterPage /> },
                {
                    path: ROUTE.DOCUMENT + '/*',
                    element: <DocumentPage />,
                },
                {
                    /** tenant required */
                    element: <AppRoot />,
                    children: [
                        { index: true, element: <POSApplication /> },
                        {
                            path: ROUTE.CUSTOMER_FACING_DISPLAY + '/*',
                            element: <CustomerFacingDisplayPage />,
                        },
                        {
                            path: '*',
                            element: <POSApplication />,
                        },
                    ],
                },
            ],
        },
    ],
    // Self-host serves this SPA under /app or /app/:subDomain (path-based
    // tenancy — see orderwire-launcher's server/routes/static.ts, which
    // computes REACT_APP_APP_PATH per request from the resolved tenant).
    // Cloud sets it to '/', where a basename is a no-op.
    { basename: getEnv('REACT_APP_APP_PATH') },
)
createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
        <PostHogProvider
            apiKey={getEnv('REACT_APP_PUBLIC_POSTHOG_KEY')}
            options={{
                api_host: getEnv('REACT_APP_PUBLIC_POSTHOG_HOST'),
                loaded: (ph) => {
                    // Only capture events in Production, UAT, and Testing.
                    if (
                        serverEnvironment === 'Production' ||
                        serverEnvironment === 'UAT' ||
                        serverEnvironment === 'Testing'
                    ) {
                        ph.opt_in_capturing()
                    } else {
                        ph.opt_out_capturing()
                    }
                },
                session_recording: {
                    // Unmask all input fields except for passwords.
                    // https://posthog.com/docs/session-replay/privacy#input-elements
                    maskAllInputs: false,
                    maskInputOptions: {
                        password: true,
                    },
                },
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <RouterProvider router={router} />
            </LocalizationProvider>
        </PostHogProvider>
    </Provider>,
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorkerRegistration.register()
